/**
 * O formulário da prescrição — um por modalidade.
 *
 * Cinco formatos sem campo em comum além do discriminador, e é por isso que não
 * existe um formulário genérico aqui: "séries e repetições" e "distância e
 * ritmo" não são o mesmo campo com rótulo diferente.
 *
 * **Quem valida é o servidor.** Os limites daqui (`min`, `max`) existem para o
 * teclado do celular abrir no modo certo e para o erro aparecer antes do envio;
 * a recusa que vale é a da união discriminada, e ela é mais estrita — recusa
 * campo de outra modalidade, faixa invertida e prescrição sem alvo nenhum.
 *
 * **Campo vazio vira `null`, não zero.** Zero é uma prescrição ("descanse zero
 * segundos"); vazio é "não estipulei". Um campo numérico que devolvesse `0` ao
 * ser apagado transformaria a segunda coisa na primeira.
 */

import { useTranslation } from 'react-i18next';

import type { IntensityLabel, Stroke } from '@/features/templates/api';
import type { DraftPrescription, DraftSwimSet } from '@/features/templates/draft';

const INTENSITIES: ReadonlyArray<IntensityLabel> = [
  'easy',
  'comfortable',
  'moderate',
  'hard',
  'very_hard',
];

const STROKES: ReadonlyArray<Stroke> = [
  'freestyle',
  'backstroke',
  'breaststroke',
  'butterfly',
  'medley',
  'kick',
  'drill',
  'mixed',
];

const FORMATS = ['interval', 'amrap', 'for_time', 'circuit'] as const;
const SIDES = ['both', 'left', 'right', 'alternating'] as const;

/** As três piscinas do P0. 25 yd é 22,86 m e não cabe num campo de metros. */
const POOLS = [
  { length: 25, unit: 'm' },
  { length: 50, unit: 'm' },
  { length: 25, unit: 'yd' },
] as const;

interface Props {
  value: DraftPrescription;
  onChange: (value: DraftPrescription) => void;
}

function Numero({
  label,
  value,
  onChange,
  min,
  max,
  step,
  optional,
}: {
  label: string;
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number;
  optional?: boolean;
}) {
  const { t } = useTranslation(['templates']);

  return (
    <label className="block">
      <span className="text-sm font-medium text-(--color-sand-800)">
        {label}
        {optional ? (
          <span className="font-normal text-(--color-sand-600)">
            {' '}
            {t('templates:field.optional')}
          </span>
        ) : null}
      </span>
      <input
        type="number"
        inputMode="numeric"
        value={value ?? ''}
        min={min}
        max={max}
        step={step}
        // Vazio é `null`, e não `0`: zero é uma prescrição, vazio é a ausência
        // dela. O `Number('')` devolve zero, e é essa a armadilha.
        onChange={(event) =>
          onChange(event.target.value === '' ? null : Number(event.target.value))
        }
        className="mt-1 w-full rounded-xl border border-(--color-sand-200) px-3 py-3"
      />
    </label>
  );
}

function Lista({
  label,
  value,
  onChange,
  options,
  prefix,
  vazio,
}: {
  label: string;
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  options: ReadonlyArray<string>;
  prefix: string;
  vazio?: string;
}) {
  const { t } = useTranslation(['templates']);

  return (
    <label className="block">
      <span className="text-sm font-medium text-(--color-sand-800)">{label}</span>
      <select
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value || null)}
        className="mt-1 w-full rounded-xl border border-(--color-sand-200) bg-(--color-sand-0) px-3 py-3"
      >
        {vazio ? <option value="">{vazio}</option> : null}
        {options.map((opcao) => (
          <option key={opcao} value={opcao}>
            {t(`${prefix}.${opcao}`)}
          </option>
        ))}
      </select>
    </label>
  );
}

export function PrescriptionForm({ value, onChange }: Props) {
  const { t } = useTranslation(['templates']);
  const grade = 'mt-3 grid grid-cols-2 gap-3';

  if (value.type === 'strength') {
    return (
      <div className={grade}>
        <Numero
          label={t('templates:prescriptionField.sets')}
          value={value.sets}
          onChange={(v) => onChange({ ...value, sets: v })}
          min={1}
          max={20}
        />
        <Numero
          label={t('templates:prescriptionField.rest')}
          value={value.rest_seconds}
          onChange={(v) => onChange({ ...value, rest_seconds: v })}
          min={0}
          max={3600}
          optional
        />
        <Numero
          label={t('templates:prescriptionField.repsMin')}
          value={value.reps_min}
          onChange={(v) => onChange({ ...value, reps_min: v })}
          min={1}
          max={200}
        />
        <Numero
          label={t('templates:prescriptionField.repsMax')}
          value={value.reps_max}
          onChange={(v) => onChange({ ...value, reps_max: v })}
          min={1}
          max={200}
        />
        <Numero
          label={t('templates:prescriptionField.load')}
          value={value.target_load_kg}
          onChange={(v) => onChange({ ...value, target_load_kg: v })}
          min={0}
          max={500}
          step={0.5}
          optional
        />
        <Numero
          label={t('templates:prescriptionField.rpe')}
          value={value.target_rpe}
          onChange={(v) => onChange({ ...value, target_rpe: v })}
          min={1}
          max={10}
          optional
        />
      </div>
    );
  }

  if (value.type === 'cardio') {
    return (
      <>
        <div className={grade}>
          <Numero
            label={t('templates:prescriptionField.durationMin')}
            value={value.target_duration_seconds ? value.target_duration_seconds / 60 : null}
            // A pessoa pensa em minutos; o banco guarda segundos. A conversão
            // fica aqui para o campo não pedir "1200".
            onChange={(v) =>
              onChange({ ...value, target_duration_seconds: v === null ? null : v * 60 })
            }
            min={1}
            max={1440}
            optional
          />
          <Numero
            label={t('templates:prescriptionField.distanceKm')}
            value={value.target_distance_m ? value.target_distance_m / 1000 : null}
            onChange={(v) =>
              onChange({ ...value, target_distance_m: v === null ? null : Math.round(v * 1000) })
            }
            min={0.1}
            step={0.1}
            optional
          />
          <Numero
            label={t('templates:prescriptionField.rpeMin')}
            value={value.target_rpe_min}
            onChange={(v) => onChange({ ...value, target_rpe_min: v })}
            min={1}
            max={10}
            optional
          />
          <Numero
            label={t('templates:prescriptionField.rpeMax')}
            value={value.target_rpe_max}
            onChange={(v) => onChange({ ...value, target_rpe_max: v })}
            min={1}
            max={10}
            optional
          />
        </div>
        <div className="mt-3">
          <Lista
            label={t('templates:prescriptionField.intensity')}
            value={value.intensity_label}
            onChange={(v) => onChange({ ...value, intensity_label: v as IntensityLabel | null })}
            options={INTENSITIES}
            prefix="templates:intensity"
            vazio={t('templates:field.notSet')}
          />
        </div>
      </>
    );
  }

  if (value.type === 'functional') {
    return (
      <>
        <div className={grade}>
          <Numero
            label={t('templates:prescriptionField.rounds')}
            value={value.rounds}
            onChange={(v) => onChange({ ...value, rounds: v })}
            min={1}
            max={50}
          />
          <Numero
            label={t('templates:prescriptionField.work')}
            value={value.work_seconds}
            onChange={(v) => onChange({ ...value, work_seconds: v })}
            min={1}
            optional
          />
          <Numero
            label={t('templates:prescriptionField.rest')}
            value={value.rest_seconds}
            onChange={(v) => onChange({ ...value, rest_seconds: v })}
            min={0}
            optional
          />
          <Numero
            label={t('templates:prescriptionField.targetReps')}
            value={value.target_reps}
            onChange={(v) => onChange({ ...value, target_reps: v })}
            min={1}
            max={500}
            optional
          />
        </div>
        <div className="mt-3">
          <Lista
            label={t('templates:prescriptionField.format')}
            value={value.format ?? 'interval'}
            onChange={(v) =>
              onChange({ ...value, format: (v ?? 'interval') as (typeof FORMATS)[number] })
            }
            options={FORMATS}
            prefix="templates:format"
          />
        </div>
      </>
    );
  }

  if (value.type === 'mobility') {
    return (
      <>
        <div className={grade}>
          <Numero
            label={t('templates:prescriptionField.hold')}
            value={value.hold_seconds}
            onChange={(v) => onChange({ ...value, hold_seconds: v })}
            min={1}
            max={3600}
            optional
          />
          <Numero
            label={t('templates:prescriptionField.repetitions')}
            value={value.repetitions}
            onChange={(v) => onChange({ ...value, repetitions: v })}
            min={1}
            max={200}
            optional
          />
          <Numero
            label={t('templates:prescriptionField.durationMin')}
            value={value.duration_seconds ? value.duration_seconds / 60 : null}
            onChange={(v) => onChange({ ...value, duration_seconds: v === null ? null : v * 60 })}
            min={1}
            optional
          />
        </div>
        <div className="mt-3">
          <Lista
            label={t('templates:prescriptionField.side')}
            value={value.side ?? 'both'}
            onChange={(v) => onChange({ ...value, side: (v ?? 'both') as (typeof SIDES)[number] })}
            options={SIDES}
            prefix="templates:side"
          />
        </div>
      </>
    );
  }

  // Natação. A piscina é um par, e não dois campos independentes: 50 jardas não
  // existe no P0, e dois selects deixariam a pessoa montar essa combinação para
  // o servidor recusar depois.
  const piscinaAtual = `${value.pool_length}-${value.pool_unit}`;
  return (
    <>
      <div className="mt-3">
        <label className="block">
          <span className="text-sm font-medium text-(--color-sand-800)">
            {t('templates:prescriptionField.pool')}
          </span>
          <select
            value={piscinaAtual}
            onChange={(event) => {
              const escolhida = POOLS.find((p) => `${p.length}-${p.unit}` === event.target.value);
              if (escolhida)
                onChange({ ...value, pool_length: escolhida.length, pool_unit: escolhida.unit });
            }}
            className="mt-1 w-full rounded-xl border border-(--color-sand-200) bg-(--color-sand-0) px-3 py-3"
          >
            {POOLS.map((piscina) => (
              <option
                key={`${piscina.length}-${piscina.unit}`}
                value={`${piscina.length}-${piscina.unit}`}
              >
                {piscina.length} {piscina.unit}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className={grade}>
        <Numero
          label={t('templates:prescriptionField.distanceM')}
          value={value.target_distance_m}
          onChange={(v) => onChange({ ...value, target_distance_m: v })}
          min={1}
          optional
        />
        <Numero
          label={t('templates:prescriptionField.rpe')}
          value={value.target_rpe}
          onChange={(v) => onChange({ ...value, target_rpe: v })}
          min={1}
          max={10}
          optional
        />
      </div>

      <SwimSets value={value.sets ?? []} onChange={(sets) => onChange({ ...value, sets })} />
    </>
  );
}

function SwimSets({
  value,
  onChange,
}: {
  value: DraftSwimSet[];
  onChange: (sets: DraftSwimSet[]) => void;
}) {
  const { t } = useTranslation(['templates']);

  return (
    <section className="mt-4">
      <h4 className="text-sm font-semibold text-(--color-sand-800)">
        {t('templates:prescriptionField.swimSets')}
      </h4>

      {value.map((serie, indice) => (
        <div key={indice} className="mt-2 rounded-xl border border-(--color-sand-200) p-3">
          <div className="grid grid-cols-2 gap-3">
            <Numero
              label={t('templates:prescriptionField.repetitions')}
              value={serie.repetitions}
              onChange={(v) =>
                onChange(value.map((s, i) => (i === indice ? { ...s, repetitions: v } : s)))
              }
              min={1}
              max={100}
            />
            <Numero
              label={t('templates:prescriptionField.distanceM')}
              value={serie.distance_m}
              onChange={(v) =>
                onChange(value.map((s, i) => (i === indice ? { ...s, distance_m: v } : s)))
              }
              min={1}
            />
          </div>
          <div className="mt-3">
            <Lista
              label={t('templates:prescriptionField.stroke')}
              value={serie.stroke ?? 'freestyle'}
              onChange={(v) =>
                onChange(
                  value.map((s, i) =>
                    i === indice ? { ...s, stroke: (v ?? 'freestyle') as Stroke } : s,
                  ),
                )
              }
              options={STROKES}
              prefix="templates:stroke"
            />
          </div>
          <button
            type="button"
            onClick={() => onChange(value.filter((_, i) => i !== indice))}
            className="mh-button mh-button--ghost mt-2 text-sm"
          >
            {t('templates:action.removeSet')}
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={() =>
          onChange([...value, { repetitions: 4, distance_m: 50, stroke: 'freestyle' }])
        }
        className="mh-button mh-button--ghost mt-2 text-sm"
      >
        {t('templates:action.addSet')}
      </button>
    </section>
  );
}
